<script setup>
import { ref, watch, defineProps, defineEmits, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

import WithLabel from 'v3/components/Form/WithLabel.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
  csatEnabled: { type: Boolean, required: true },
});

onMounted(() => {
  // eslint-disable-next-line no-console
  console.log('🟢 [CSAT-EVALUATION] Component mounted!', {
    csatEnabled: props.csatEnabled,
    showField: !props.csatEnabled,
    inbox: props.inbox?.id,
  });
});

watch(() => props.csatEnabled, (newVal) => {
  // eslint-disable-next-line no-console
  console.log('🟡 [CSAT-EVALUATION] csatEnabled changed:', newVal, 'showField:', !newVal);
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const store = useStore();
const isUpdating = ref(false);

// Initialize from additional_attributes
const evaluationMessage = ref(
  props.inbox?.additional_attributes?.evaluation || ''
);

// Watch for inbox changes
watch(
  () => props.inbox?.additional_attributes?.evaluation,
  newValue => {
    if (newValue !== undefined) {
      evaluationMessage.value = newValue;
    }
  }
);

const updateInbox = async attributes => {
  const payload = {
    id: props.inbox.id,
    formData: false,
    ...attributes,
  };

  return store.dispatch('inboxes/updateInbox', payload);
};

const saveEvaluation = async () => {
  try {
    isUpdating.value = true;

    const currentAttrs = props.inbox.channel?.additional_attributes || {};

    await updateInbox({
      channel: {
        additional_attributes: {
          ...currentAttrs,
          evaluation: evaluationMessage.value,
        },
      },
    });

    useAlert(t('QUEPASA_INBOX.CSAT_EVALUATION.SUCCESS_MESSAGE'));
    emit('update');
  } catch (error) {
    useAlert(t('QUEPASA_INBOX.CSAT_EVALUATION.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <div v-if="!csatEnabled" class="mt-6 pt-6 border-t border-n-slate-6">
    <div class="mb-4">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('QUEPASA_INBOX.CSAT_EVALUATION.TITLE') }}
      </h3>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ $t('QUEPASA_INBOX.CSAT_EVALUATION.DESCRIPTION') }}
      </p>
    </div>

    <div class="grid gap-5">
      <WithLabel
        :label="$t('QUEPASA_INBOX.CSAT_EVALUATION.MESSAGE.LABEL')"
        name="evaluation"
      >
        <Editor
          v-model="evaluationMessage"
          :placeholder="$t('QUEPASA_INBOX.CSAT_EVALUATION.MESSAGE.PLACEHOLDER')"
          :max-length="500"
          class="w-full"
        />
      </WithLabel>

      <div>
        <NextButton
          type="button"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :is-loading="isUpdating"
          @click="saveEvaluation"
        />
      </div>
    </div>
  </div>
</template>
